/**
 * DHI Hospitality Landing Page
 * Section 5 from dhi_spec_v3.docx
 * Two-column layout with hero copy and form card
 */

import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";

const CURRENCIES = [
  'USD', 'EUR', 'GBP',
  'AED', 'AUD', 'BHD', 'BND', 'BRL', 'CAD', 'CHF', 'CNY', 
  'EGP', 'FJD', 'HKD', 'IDR', 'INR', 'JOD', 'JPY', 'KHR', 
  'KRW', 'KWD', 'LKR', 'MMK', 'MUR', 'MVR', 'MYR', 'NZD', 
  'OMR', 'PHP', 'QAR', 'SAR', 'SCR', 'SGD', 'THB', 'TRY', 
  'TWD', 'VND', 'ZAR'
];

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" style={{ width: 10, height: 10 }}>
    <path d="M2.5 6.5L4.5 8.5L9.5 3.5" stroke="#2D4DE0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const UrlIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" style={{ width: 12, height: 12 }}>
    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M4 6h4M6 4v4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" style={{ width: 12, height: 12 }}>
    <path d="M2 3h8a1 1 0 011 1v5a1 1 0 01-1 1H2a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M1 4l5 3.5L11 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

// Refined Luxury Drop Icon for the 'Leaking' effect
const DropIcon = () => (
  <svg className="leaky-drop" width="14" height="20" viewBox="0 0 14 20" fill="none" style={{ display: 'inline-block', marginLeft: '4px', verticalAlign: 'middle' }}>
    <path d="M7 20C10.866 20 14 16.866 14 13C14 9.13401 7 0 7 0C7 0 0 9.13401 0 13C0 16.866 3.13401 20 7 20Z" fill="url(#drop-grad)"/>
    <defs>
      <linearGradient id="drop-grad" x1="7" y1="0" x2="7" y2="20" gradientUnits="userSpaceOnUse">
        <stop stopColor="#f0ce5c"/>
        <stop offset="1" stopColor="#b8a140"/>
      </linearGradient>
    </defs>
  </svg>
);

const ICON_PROPS = {
  width: 64,
  height: 64,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "#2D4DE0",
  strokeWidth: 1.3,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const PARAMS = [
  { n:1, name:'Brand Visibility & Dominance', desc:'Are guests finding your hotel before OTAs rank above you on Google?',
    icon:(
      <svg {...ICON_PROPS}>
        <circle cx="10.5" cy="10.5" r="6.5"/>
        <path d="M20 20l-4.9-4.9"/>
        <path d="M7.5 11.5l2-2 1.5 1.5 2.5-3"/>
      </svg>
    ) },
  { n:2, name:'Rate Parity & Channel Competitiveness', desc:'Is booking direct always cheaper than booking through an OTA?',
    icon:(
      <svg {...ICON_PROPS}>
        <path d="M12 3v18"/>
        <path d="M5 8h14"/>
        <path d="M8 8l-3 6h6z"/>
        <path d="M16 8l-3 6h6z"/>
        <path d="M9 21h6"/>
      </svg>
    ) },
  { n:3, name:'Google Business Profile Effectiveness', desc:'Is your Google Business Profile optimised to convert guests on Google Maps and Search?',
    icon:(
      <svg {...ICON_PROPS}>
        <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z"/>
        <circle cx="12" cy="9" r="2.5"/>
      </svg>
    ) },
  { n:4, name:'Page Load Performance', desc:'Are guests leaving before your hotel website loads on mobile?',
    icon:(
      <svg {...ICON_PROPS}>
        <path d="M3.5 14a8.5 8.5 0 1 1 17 0"/>
        <path d="M12 14l4-3.5"/>
        <circle cx="12" cy="14" r="1.1" fill="#2D4DE0"/>
        <path d="M5 17.5h14"/>
      </svg>
    ) },
  { n:5, name:'Mobile Booking Experience', desc:'Can guests complete a direct booking on mobile without friction?',
    icon:(
      <svg {...ICON_PROPS}>
        <rect x="6.5" y="2.5" width="11" height="19" rx="2.2"/>
        <path d="M10.5 5.5h3"/>
        <path d="M11 18.5h2"/>
        <path d="M9 9.5h6"/>
        <path d="M9 12.5h6"/>
      </svg>
    ) },
  { n:6, name:'Booking Simplicity & Friction', desc:'How many steps does a guest need to complete a direct booking?',
    icon:(
      <svg {...ICON_PROPS}>
        <path d="M5 3l4 16 2.5-6 6-2.5z"/>
        <path d="M14 14l5 5"/>
      </svg>
    ) },
  { n:7, name:'Direct Booking Incentives', desc:'Are you giving guests a compelling reason to book direct over OTAs?',
    icon:(
      <svg {...ICON_PROPS}>
        <path d="M6 3h12l3.5 5.5L12 21 2.5 8.5z"/>
        <path d="M2.5 8.5h19"/>
        <path d="M10 3l-2 5.5L12 21l4-12.5-2-5.5"/>
      </svg>
    ) },
  { n:8, name:'Content Depth & Quality', desc:'Does your content answer the questions guests need answered before booking?',
    icon:(
      <svg {...ICON_PROPS}>
        <path d="M14 2.5H6.5a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V8z"/>
        <path d="M14 2.5V8h5.5"/>
        <path d="M8 13h8"/>
        <path d="M8 16.5h8"/>
        <path d="M8 9.5h3"/>
      </svg>
    ) },
  { n:9, name:'Social Proof & Review Signals', desc:'Is your review reputation actively working as a conversion tool?',
    icon:(
      <svg {...ICON_PROPS}>
        <path d="M12 2.5l2.9 6.4 7 .7-5.3 4.7 1.6 6.8L12 17.6l-6.2 3.5 1.6-6.8L2.1 9.6l7-.7z"/>
      </svg>
    ) },
  { n:10, name:'Payment Trust & Security', desc:'Do guests feel safe transacting directly on your hotel website?',
    icon:(
      <svg {...ICON_PROPS}>
        <path d="M12 21.5s7.5-3.5 7.5-9.5V5L12 2.5 4.5 5v7c0 6 7.5 9.5 7.5 9.5z"/>
        <path d="M8.5 12l2.5 2.5 4.5-5"/>
      </svg>
    ) },
];

const LandingPage = ({ onSubmit, isLoading }) => {
  const [searchParams] = useSearchParams();

  const urlParam      = searchParams.get('url');
  const emailParam    = searchParams.get('email');
  const roomsParam    = searchParams.get('rooms');
  const adrParam      = searchParams.get('adr');
  const losParam      = searchParams.get('los');
  const currencyParam = searchParams.get('currency');
  const hasAllParams  = !!(urlParam && emailParam && roomsParam && adrParam && losParam);

  const [formData, setFormData] = useState({
    url: "",
    email: "",
    rooms: "",
    adr: "",
    currency: "USD",
    los: "",
    termsAccepted: false
  });
  const [errors, setErrors] = useState({});
  const [activeParam, setActiveParam] = useState(0);
  const [activeKmTab, setActiveKmTab] = useState(0);

  const faqSectionRef = useRef(null);
  const faqHeaderRef = useRef(null);
  const faqListRef = useRef(null);
  const folioPausedRef = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const section = faqSectionRef.current;
      const header = faqHeaderRef.current;
      const list = faqListRef.current;
      if (!section || !header || !list) return;

      const scrollY = window.scrollY;
      const navOffset = 100;
      const sectionAbsTop = section.getBoundingClientRect().top + scrollY;

      // Start moving when section top hits the nav bar
      const start = sectionAbsTop - navOffset;
      // Stop when header bottom aligns with list bottom (last FAQ item)
      const maxTranslate = list.offsetHeight - header.offsetHeight;

      const translate = Math.min(Math.max(scrollY - start, 0), maxTranslate);
      header.style.transform = `translateY(${translate}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const nextParam = () => setActiveParam((prev) => (prev + 1) % PARAMS.length);
  const prevParam = () => setActiveParam((prev) => (prev - 1 + PARAMS.length) % PARAMS.length);

  useEffect(() => {
    const id = setInterval(() => {
      if (!folioPausedRef.current) setActiveParam((prev) => (prev + 1) % PARAMS.length);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') nextParam();
      if (e.key === 'ArrowLeft')  prevParam();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!hasAllParams) return;
    let normalizedUrl = urlParam;
    if (!normalizedUrl.startsWith('http')) normalizedUrl = `https://${normalizedUrl}`;
    onSubmit({
      url: normalizedUrl,
      email: emailParam,
      hotelType: 'city',
      rooms: Number(roomsParam),
      adr: Number(adrParam),
      currency: currencyParam || 'USD',
      los: Number(losParam),
      termsAccepted: true,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.url) {
      newErrors.url = "Website URL is required";
    } else {
      try {
        const urlToCheck = formData.url.startsWith("http") ? formData.url : `https://${formData.url}`;
        const parsed = new URL(urlToCheck);
        if (!parsed.hostname || !parsed.hostname.includes('.')) {
          newErrors.url = "Please enter a valid website URL";
        }
      } catch {
        newErrors.url = "Please enter a valid website URL";
      }
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    } else {
      const freeEmailDomains = [
        "gmail.com", "yahoo.com", "hotmail.com", "outlook.com",
        "icloud.com", "aol.com", "protonmail.com", "mail.com",
        "ymail.com", "yahoo.co.in", "yahoo.co.uk", "googlemail.com",
        "live.com", "msn.com", "me.com", "mac.com"
      ];
      const emailDomain = formData.email.split("@")[1]?.toLowerCase();
      if (emailDomain && freeEmailDomains.includes(emailDomain)) {
        newErrors.email = "Please use your work email";
      }
    }

    if (!formData.rooms) {
      newErrors.rooms = "Required";
    } else if (isNaN(formData.rooms) || Number(formData.rooms) < 1) {
      newErrors.rooms = "Invalid";
    }

    if (!formData.adr) {
      newErrors.adr = "Required";
    } else if (isNaN(formData.adr) || Number(formData.adr) < 1) {
      newErrors.adr = "Invalid";
    }

    if (!formData.los) {
      newErrors.los = "Required";
    } else if (isNaN(formData.los) || Number(formData.los) < 0.5) {
      newErrors.los = "Invalid";
    }

    if (!formData.termsAccepted) {
      newErrors.termsAccepted = "Please accept the terms";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      let normalizedUrl = formData.url;
      if (!normalizedUrl.startsWith("http")) normalizedUrl = `https://${normalizedUrl}`;
      onSubmit({
        url: normalizedUrl,
        email: formData.email,
        hotelType: "city",
        rooms: Number(formData.rooms),
        adr: Number(formData.adr),
        currency: formData.currency,
        los: Number(formData.los),
        termsAccepted: formData.termsAccepted,
      });
    }
  };

  const handleChange = (field, value) => {
    // Prevent negative numbers for specific fields
    if (["rooms", "adr", "los"].includes(field)) {
      if (value !== "" && Number(value) < 0) return;
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const scrollToForm = () => {
    document.querySelector('.form-card')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (hasAllParams) return null;

  return (
    <div id="landing" className="landing-page" data-testid="landing-page">
      <Navbar />

      <div className="landing-inner">
        {/* LEFT COLUMN */}
        <div className="landing-left">
          <div className="l-eyebrow">
            <div className="l-eyebrow-dot"></div>
            <span className="l-eyebrow-text">Hotel Digital Audit</span>
          </div>

          <h1 className="l-headline">
            Is your hotel website<br/>
            <em className="l-leaking">
              losing revenue?
            </em>
          </h1>

          <p className="l-sub">
            2X your direct bookings from your existing website traffic. AI-powered audit across 10 parameters in 45 seconds.
          </p>

          <div className="l-bullets">
            <div className="l-bullet">
              <div className="l-bullet-icon"><CheckIcon /></div>
              See your estimated direct revenue opportunity in real-time
            </div>
            <div className="l-bullet">
              <div className="l-bullet-icon"><CheckIcon /></div>
              Benchmark against 1,000+ hotel websites audited
            </div>
            <div className="l-bullet">
              <div className="l-bullet-icon"><CheckIcon /></div>
              Get a prioritised revenue recovery action plan
            </div>
          </div>

          <div className="l-trust">
            <div className="l-trust-item">
              <span className="l-trust-num">1,000+</span>
              <span className="l-trust-label">Hotels audited</span>
            </div>
            <div className="l-trust-div"></div>
            <div className="l-trust-item">
              <span className="l-trust-num">45s</span>
              <span className="l-trust-label">Avg. audit time</span>
            </div>
            <div className="l-trust-div"></div>
            <div className="l-trust-item">
              <span className="l-trust-num">10</span>
              <span className="l-trust-label">Parameters scored</span>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - FORM */}
        <div className="form-card" data-testid="form-card">
          <div className="form-title">Request Your Audit</div>

          <form onSubmit={handleSubmit} data-testid="audit-form">
            <div className="form-group">
              <label className="form-label"><UrlIcon />Hotel Website URL</label>
              <input
                type="url"
                data-testid="url-input"
                className="form-input"
                placeholder="https://yourhotel.com"
                value={formData.url}
                onChange={(e) => handleChange("url", e.target.value)}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && !v.startsWith("http://") && !v.startsWith("https://")) {
                    handleChange("url", "https://" + v);
                  }
                }}
                disabled={isLoading}
                style={errors.url ? { borderColor: 'var(--red)' } : {}}
              />
              {errors.url && <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{errors.url}</p>}
            </div>

            <div className="form-group">
              <label className="form-label"><EmailIcon />Email Address</label>
              <input
                type="email"
                data-testid="email-input"
                className="form-input"
                placeholder="you@yourhotel.com"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                disabled={isLoading}
                style={errors.email ? { borderColor: 'var(--red)' } : {}}
              />
              {errors.email && <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{errors.email}</p>}
            </div>

            <div className="form-row-with-labels">
              <div className="form-field-group">
                <label className="form-label">Room Count</label>
                <input
                  type="number"
                  data-testid="rooms-input"
                  className="form-input"
                  placeholder="e.g. 150"
                  min="1"
                  value={formData.rooms}
                  onChange={(e) => handleChange("rooms", e.target.value)}
                  disabled={isLoading}
                  style={errors.rooms ? { borderColor: 'var(--red)' } : {}}
                />
              </div>
              <div className="form-field-group">
                <label className="form-label">Currency</label>
                <select
                  data-testid="currency-select"
                  className="form-input"
                  value={formData.currency}
                  onChange={(e) => handleChange("currency", e.target.value)}
                  disabled={isLoading}
                >
                  {CURRENCIES.map(code => <option key={code} value={code}>{code}</option>)}
                </select>
              </div>
              <div className="form-field-group">
                <label className="form-label">ADR</label>
                <input
                  type="number"
                  data-testid="adr-input"
                  className="form-input"
                  placeholder="e.g. 250"
                  min="1"
                  value={formData.adr}
                  onChange={(e) => handleChange("adr", e.target.value)}
                  disabled={isLoading}
                  style={errors.adr ? { borderColor: 'var(--red)' } : {}}
                />
              </div>
              <div className="form-field-group">
                <label className="form-label">Avg. LOS</label>
                <input
                  type="number"
                  data-testid="los-input"
                  className="form-input"
                  placeholder="e.g. 2.5"
                  min="0.5"
                  step="0.1"
                  value={formData.los}
                  onChange={(e) => handleChange("los", e.target.value)}
                  disabled={isLoading}
                  style={errors.los ? { borderColor: 'var(--red)' } : {}}
                />
              </div>
            </div>

            <div className="form-terms">
              <input
                type="checkbox"
                id="terms"
                data-testid="terms-checkbox"
                checked={formData.termsAccepted}
                onChange={(e) => handleChange("termsAccepted", e.target.checked)}
                disabled={isLoading}
              />
              <label htmlFor="terms">
                I agree to the <a href="https://dhihospitality.com/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and <a href="https://dhihospitality.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </label>
            </div>

            {errors.termsAccepted && <p style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12 }}>{errors.termsAccepted}</p>}

            <button
              type="submit"
              data-testid="submit-audit-btn"
              className="form-cta"
              disabled={isLoading}
              style={{ opacity: isLoading ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 8 }}
            >
              {isLoading ? (
                <>
                  <span style={{ width: 16, height: 16, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                  Analyzing...
                </>
              ) : (
                <>Get Your Full Action Plan <ArrowIcon /></>
              )}
            </button>

            <div className="form-footer">
              Enterprise-grade data · No commitment required
            </div>
          </form>
        </div>
      </div>

      {/* FOLD 2 — Audit output preview */}
      <div className="fold-dark">
        <div className="fold-inner">
          <p className="feyebrow">What you receive</p>
          <h2 className="fh2">A full hotel digital audit, with a revenue figure at every level.</h2>
          <p className="fsub">A property-specific breakdown of your direct booking performance, scored across every channel that influences a guest's booking decision.</p>
          <div style={{ background:'#F5F6FA', borderRadius:16, overflow:'hidden', border:'1px solid rgba(0,0,0,0.06)' }}>
            {/* Browser chrome */}
            <div style={{ background:'#fff', borderBottom:'1px solid rgba(0,0,0,0.06)', padding:'10px 16px', display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ width:10, height:10, borderRadius:'50%', background:'#FF5F57', display:'inline-block' }}/>
              <span style={{ width:10, height:10, borderRadius:'50%', background:'#FFBD2E', display:'inline-block', marginLeft:4 }}/>
              <span style={{ width:10, height:10, borderRadius:'50%', background:'#28CA41', display:'inline-block', marginLeft:4 }}/>
              <span style={{ fontSize:12, color:'rgba(10,10,26,0.3)', marginLeft:10 }}>audit.dhihospitality.com/share/...</span>
            </div>
            <div style={{ height:3, background:'linear-gradient(90deg,#2D4DE0,#4A6FE3,rgba(45,77,224,0))' }}/>
            <div style={{ padding:20 }}>
              {/* Row 1: Score + Hotel summary */}
              <div className="dash-row1">
                <div style={{ background:'#fff', borderRadius:14, padding:'24px 20px', textAlign:'center', border:'1px solid rgba(10,10,26,0.08)', borderLeft:'3px solid #2D4DE0', boxShadow:'0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(10,10,26,0.45)', marginBottom:16 }}>HOTEL DIGITAL AUDIT SCORE</div>
                  <svg width="130" height="130" viewBox="0 0 130 130" style={{ display:'block', margin:'0 auto 12px' }}>
                    <circle cx="65" cy="65" r="50" fill="none" stroke="#F5F6FA" strokeWidth="8"/>
                    <circle cx="65" cy="65" r="50" fill="none" stroke="#2D4DE0" strokeWidth="8" strokeDasharray="131.9 182.2" strokeDashoffset="0" strokeLinecap="round" transform="rotate(-90 65 65)"/>
                    <text x="65" y="60" textAnchor="middle" fontFamily="Outfit,sans-serif" fontSize="36" fontWeight="800" fill="#0A0F2E">42</text>
                    <text x="65" y="78" textAnchor="middle" fontSize="12" fill="rgba(10,10,26,0.45)">out of 100</text>
                  </svg>
                  <div style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#F0F1F7', color:'#0A0F2E', fontSize:10, fontWeight:700, padding:'4px 11px', borderRadius:20, marginBottom:10 }}>Weak</div>
                  <div style={{ fontSize:12, color:'rgba(10,10,26,0.52)' }}>Underperforms vs <strong style={{ color:'#0A0A1A', fontWeight:700 }}>58%</strong> of industry benchmarks</div>
                </div>
                <div style={{ background:'#fff', borderRadius:14, padding:24, border:'1px solid rgba(10,10,26,0.08)', borderLeft:'3px solid #2D4DE0', boxShadow:'0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize:20, fontWeight:800, color:'#0A0F2E', marginBottom:6, fontFamily:"'Outfit',sans-serif" }}>Hard Rock Hotel New York</div>
                  <div style={{ fontSize:12, color:'rgba(10,10,26,0.35)', marginBottom:16 }}>New York City, United States · 446 rooms · USD 620 ADR</div>
                  <div style={{ fontSize:13, color:'rgba(10,10,26,0.52)', lineHeight:1.7, marginBottom:16 }}>Your website's direct booking performance has been benchmarked across 10 data points covering search visibility, booking experience, and conversion strength.</div>
                  <div style={{ fontSize:13, color:'rgba(10,10,26,0.52)', marginBottom:12 }}>Estimated annual revenue opportunity: <strong style={{ color:'#1C2470', fontSize:18, fontFamily:"'Outfit',sans-serif", fontWeight:800 }}>$15.8M</strong></div>
                  <div style={{ color:'#2D4DE0', fontSize:13, fontWeight:600, cursor:'pointer' }}>View prioritised action plan →</div>
                </div>
              </div>
              {/* Row 2: Metric cards */}
              <div className="dash-row2">
                <div style={{ background:'#fff', borderRadius:14, padding:20, border:'1px solid rgba(10,10,26,0.08)', borderLeft:'3px solid #2D4DE0', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(10,10,26,0.45)', marginBottom:10 }}>POTENTIAL VISITS</div>
                  <div style={{ fontSize:28, fontWeight:800, color:'#0A0A1A', fontFamily:"'Outfit',sans-serif", marginBottom:4 }}>56,642</div>
                  <div style={{ fontSize:12, color:'rgba(10,10,26,0.45)' }}>vs current 27,074/mo</div>
                </div>
                <div style={{ background:'#fff', borderRadius:14, padding:20, border:'1px solid rgba(10,10,26,0.08)', borderLeft:'3px solid #2D4DE0', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(10,10,26,0.45)', marginBottom:10 }}>POTENTIAL CONVERSION</div>
                  <div style={{ fontSize:28, fontWeight:800, color:'#0A0A1A', fontFamily:"'Outfit',sans-serif", marginBottom:4 }}>1.50%</div>
                  <div style={{ fontSize:12, color:'rgba(10,10,26,0.45)' }}>vs current 0.41%</div>
                </div>
                <div style={{ background:'#0F2A8A', borderRadius:14, padding:20, boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(255,255,255,0.5)', marginBottom:10 }}>REVENUE OPPORTUNITY</div>
                  <div style={{ fontSize:26, fontWeight:800, color:'#ffffff', fontFamily:"'Outfit',sans-serif", marginBottom:4, letterSpacing:'-1px' }}>$15.8M</div>
                  <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)' }}>Annual estimate</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA STRIP 2 — after dashboard preview */}
      <div className="cta-strip">
        <div>
          <p className="cta-strip-text">Your direct revenue gap is measurable. Your action plan is ready.</p>
          <p className="cta-strip-sub">Run your audit in 45 seconds.</p>
        </div>
        <button className="cta-btn" onClick={() => scrollToForm()}>Run Your Audit →</button>
      </div>

      {/* FOLD 3 — Revolutionized Methodology — Forensic Pipeline */}
      <div className="fold-light methodology-section">
        <div className="fold-inner">
          <p className="feyebrow">How it works</p>
          <h2 className="fh2">How the hotel digital audit works</h2>
          <p className="fsub">Three inputs. Ten parameters scored against live data. One revenue number. Delivered in 45 seconds.</p>
          
          <div className="pipeline-container">
            {/* The Connecting Line */}
            <div className="pipeline-line">
              <div className="pipeline-flow"></div>
            </div>

            <div className="pipeline-steps">
              <div className="pipeline-step">
                <div className="p-node">
                  <div className="p-node-inner">01</div>
                  <div className="p-node-pulse"></div>
                </div>
                <div className="p-content">
                  <h3 className="p-title">Submit your hotel website URL</h3>
                  <p className="p-desc">Enter your hotel website URL and basic property data — room count, ADR, currency, and average length of stay. Takes under a minute.</p>
                </div>
              </div>

              <div className="pipeline-step">
                <div className="p-node">
                  <div className="p-node-inner">02</div>
                  <div className="p-node-pulse"></div>
                </div>
                <div className="p-content">
                  <h3 className="p-title">Your hotel is audited across 10 parameters in real time</h3>
                  <p className="p-desc">Live data is pulled from authoritative industry sources and scored across three performance tiers in real time.</p>
                </div>
              </div>

              <div className="pipeline-step">
                <div className="p-node">
                  <div className="p-node-inner">03</div>
                  <div className="p-node-pulse"></div>
                </div>
                <div className="p-content">
                  <h3 className="p-title">Receive your action plan with revenue impact per fix</h3>
                  <p className="p-desc">A scored report, a revenue opportunity in your currency, and a prioritised action plan. Every recommendation is ranked High, Medium, or Low.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="forensic-folio" onMouseEnter={() => { folioPausedRef.current = true; }} onMouseLeave={() => { folioPausedRef.current = false; }}>
            <div className="folio-header">
              <span className="folio-label">DIGITAL DOSSIER: PARAMETER {activeParam + 1} OF 10</span>
              <div className="folio-controls">
                <button onClick={prevParam} className="folio-btn">←</button>
                <button onClick={nextParam} className="folio-btn">→</button>
              </div>
            </div>
            
            <div className="folio-pages">
              {PARAMS.map((p, idx) => {
                const offset = idx - activeParam;
                let status = "far";
                if (offset === 0) status = "active";
                else if (offset === -1) status = "prev";
                else if (offset === 1) status = "next";
                else if (offset === -2) status = "prev-2";
                else if (offset === 2) status = "next-2";

                return (
                  <div
                    key={p.n}
                    className={`folio-page ${status}`}
                    onClick={() => status !== "active" && status !== "far" && setActiveParam(idx)}
                  >
                    <div className="page-front">
                      <div className="page-scanner"></div>
                      <div className="page-content-wrapper" key={status === 'active' ? `active-${p.n}` : p.n}>
                        <div className="p-icon-lg p-reveal-1">{p.icon}</div>
                        <div className="p-meta-lg">
                          <span className="p-num-lg p-reveal-2">#{String(p.n).padStart(2,'0')}</span>
                          <h3 className="p-name-lg p-reveal-3">{p.name}</h3>
                          <p className="p-desc-lg p-reveal-4">{p.desc}</p>
                        </div>
                        <div className="p-status-lg p-reveal-5">SCANNING IN PROGRESS...</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="folio-tabs">
              {PARAMS.map((p, idx) => (
                <div 
                  key={p.n} 
                  className={`folio-tab ${idx === activeParam ? 'tab-active' : ''}`}
                  onClick={() => setActiveParam(idx)}
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Pipeline Styles */
        .methodology-section { padding-bottom: 120px !important; }
        .pipeline-container { position: relative; margin: 80px 0 120px; }
        .pipeline-line { position: absolute; top: 35px; left: 10%; right: 10%; height: 2px; background: var(--ink-08); z-index: 0; overflow: hidden; }
        .pipeline-flow { position: absolute; top: 0; left: -100%; width: 100%; height: 100%; background: linear-gradient(90deg, transparent, #1A2FA8, transparent); animation: flow 3s infinite linear; }

        .pipeline-steps { display: flex; justify-content: space-between; position: relative; z-index: 1; }
        .pipeline-step { flex: 1; text-align: center; padding: 0 20px; }

        .p-node { width: 72px; height: 72px; margin: 0 auto 24px; position: relative; }
        .p-node-inner { width: 100%; height: 100%; background: #1A2FA8; border: 2px solid rgba(26,47,168,0.4); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: 'Outfit', sans-serif; font-size: 20px; font-weight: 800; color: #ffffff; position: relative; z-index: 2; box-shadow: 0 0 20px rgba(26,47,168,0.35); }
        .p-node-pulse { position: absolute; inset: -10px; border: 2px solid #1A2FA8; border-radius: 50%; opacity: 0; animation: pulse 2s infinite; }

        .p-title { font-family: 'Outfit', sans-serif; font-size: 18px; font-weight: 800; color: var(--ink); margin-bottom: 12px; }
        .p-desc { font-size: 14px; color: var(--ink-50); line-height: 1.6; max-width: 240px; margin: 0 auto; }

        /* 3D Dossier Folio */
        .forensic-folio { background: var(--off-white); border: 1px solid rgba(26,47,168,0.15); border-radius: 24px; padding: 48px; position: relative; perspective: 2000px; overflow: visible; }
        .folio-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 48px; position: relative; z-index: 20; }
        .folio-label { font-size: 11px; font-weight: 800; letter-spacing: 0.2em; color: #1A2FA8; }
        .folio-btn { background: rgba(26,47,168,0.07); border: 1px solid rgba(26,47,168,0.18); color: #1A2FA8; width: 44px; height: 44px; border-radius: 50%; cursor: pointer; transition: all 0.3s; margin-left: 12px; font-size: 18px; }
        .folio-btn:hover { background: #1A2FA8; color: #fff; transform: scale(1.1); }

        /* Forensic coverflow — active centered, neighbours visible in 3D */
        .folio-pages { position: relative; height: 360px; width: 100%; perspective: 1800px; transform-style: preserve-3d; }
        .folio-page {
          position: absolute;
          top: 0;
          left: 50%;
          width: min(62%, 620px);
          height: 100%;
          margin-left: calc(min(62%, 620px) / -2);
          transition: transform 0.42s cubic-bezier(0.22, 1, 0.36, 1),
                      opacity 0.42s cubic-bezier(0.22, 1, 0.36, 1);
          will-change: transform, opacity;
          pointer-events: none;
          opacity: 0;
        }

        .folio-page.active   { transform: translateX(0)     scale(1);    opacity: 1;    z-index: 10; pointer-events: auto; }
        .folio-page.prev     { transform: translateX(-58%)  scale(0.82); opacity: 0.5;  z-index: 6;  pointer-events: auto; cursor: pointer; }
        .folio-page.next     { transform: translateX(58%)   scale(0.82); opacity: 0.5;  z-index: 6;  pointer-events: auto; cursor: pointer; }
        .folio-page.prev-2   { transform: translateX(-102%) scale(0.62); opacity: 0.2;  z-index: 3;  pointer-events: auto; cursor: pointer; }
        .folio-page.next-2   { transform: translateX(102%)  scale(0.62); opacity: 0.2;  z-index: 3;  pointer-events: auto; cursor: pointer; }
        .folio-page.far      { opacity: 0; visibility: hidden; transform: translateX(0) scale(0.4); pointer-events: none; }

        .folio-page.prev:hover, .folio-page.next:hover { opacity: 0.75; }
        .folio-page.prev-2:hover, .folio-page.next-2:hover { opacity: 0.38; }

        /* Hide scanner & status on side cards so the centred card is the focal point */
        .folio-page:not(.active) .page-scanner,
        .folio-page:not(.active) .p-status-lg { display: none; }

        .page-front { position: absolute; inset: 0; background: #fff; border: 1px solid rgba(26,47,168,0.3); border-radius: 20px; padding: 56px; box-shadow: 0 4px 12px rgba(0,0,0,0.13), 0 12px 40px rgba(0,0,0,0.18); overflow: hidden; backface-visibility: hidden; }
        .page-front::after { content: ""; position: absolute; top: 0; left: 24px; right: 24px; height: 2px; background: linear-gradient(90deg, #1A2FA8, #2D4DE0, rgba(26,47,168,0)); border-radius: 0 0 2px 2px; }
        .page-scanner { position: absolute; top: 0; left: 0; width: 2px; height: 100%; background: #1A2FA8; box-shadow: 0 0 20px rgba(26,47,168,0.6); animation: page-scan 4s infinite linear; opacity: 0.7; z-index: 5; }

        .page-content-wrapper { display: flex; align-items: center; gap: 56px; position: relative; z-index: 10; height: 100%; }
        .p-icon-lg { font-size: 72px; background: rgba(26,47,168,0.07); width: 140px; height: 140px; border-radius: 32px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; border: 1px solid rgba(26,47,168,0.25); box-shadow: inset 0 0 20px rgba(26,47,168,0.05); }
        .p-meta-lg { flex: 1; }
        .p-num-lg { font-size: 14px; font-weight: 800; color: #1A2FA8; margin-bottom: 12px; display: block; letter-spacing: 0.1em; }
        .p-name-lg { font-family: 'Outfit', sans-serif; font-size: 32px; font-weight: 800; color: #020412; margin-bottom: 20px; line-height: 1.1; }
        .p-desc-lg { font-size: 18px; color: rgba(2,4,18,0.5); line-height: 1.7; max-width: 540px; }
        .p-status-lg { position: absolute; bottom: -20px; right: 0; font-size: 10px; font-weight: 800; color: rgba(26,47,168,0.5); letter-spacing: 0.15em; }

        .folio-tabs { display: flex; justify-content: center; gap: 10px; margin-top: 56px; position: relative; z-index: 20; }
        .folio-tab { width: 36px; height: 4px; background: var(--ink-08); border-radius: 2px; cursor: pointer; transition: all 0.4s; }
        .tab-active { background: #1A2FA8; width: 56px; }

        /* Animations */
        @keyframes flow { 0% { left: -100%; } 100% { left: 100%; } }
        @keyframes pulse { 0% { transform: scale(1); opacity: 0.5; } 100% { transform: scale(1.5); opacity: 0; } }
        @keyframes page-scan { 0% { left: -5%; } 100% { left: 105%; } }
        @keyframes p-reveal { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .folio-page:not(.active) .p-reveal-2,
        .folio-page:not(.active) .p-reveal-4,
        .folio-page:not(.active) .p-reveal-5 { opacity: 0; }
        .folio-page.active .p-reveal-1,
        .folio-page.active .p-reveal-2,
        .folio-page.active .p-reveal-3,
        .folio-page.active .p-reveal-4,
        .folio-page.active .p-reveal-5 { opacity: 0; }
        .folio-page.active .p-reveal-1 { animation: p-reveal 0.35s cubic-bezier(0.22,1,0.36,1) 0.05s forwards; }
        .folio-page.active .p-reveal-2 { animation: p-reveal 0.35s cubic-bezier(0.22,1,0.36,1) 0.13s forwards; }
        .folio-page.active .p-reveal-3 { animation: p-reveal 0.35s cubic-bezier(0.22,1,0.36,1) 0.20s forwards; }
        .folio-page.active .p-reveal-4 { animation: p-reveal 0.35s cubic-bezier(0.22,1,0.36,1) 0.28s forwards; }
        .folio-page.active .p-reveal-5 { animation: p-reveal 0.35s cubic-bezier(0.22,1,0.36,1) 0.36s forwards; }

        @media(max-width: 960px) {
          .page-content-wrapper { flex-direction: column; text-align: center; gap: 32px; }
          .p-icon-lg { width: 100px; height: 100px; font-size: 48px; }
          .p-name-lg { font-size: 24px; }
          .p-desc-lg { font-size: 15px; }
          .folio-pages { height: 480px; }
          .forensic-folio { padding: 32px; }
          /* On tablets, only show prev/next (drop prev-2/next-2) and tighten the spread */
          .folio-page { width: 80%; margin-left: -40%; }
          .folio-page.prev   { transform: translateX(-50%) scale(0.7); }
          .folio-page.next   { transform: translateX(50%)  scale(0.7); }
          .folio-page.prev-2, .folio-page.next-2 { opacity: 0; visibility: hidden; }
        }
        @media(max-width: 640px) {
          /* On phones, only the active card */
          .folio-page { width: 100%; margin-left: -50%; }
          .folio-page.prev, .folio-page.next,
          .folio-page.prev-2, .folio-page.next-2 { opacity: 0; visibility: hidden; pointer-events: none; }
        }

        @media(max-width: 768px) {
          .pipeline-line { display: none; }
          .pipeline-steps { flex-direction: column; gap: 40px; }
          .methodology-section { padding-bottom: 80px !important; }
        }
      `}</style>

      {/* FOLD 4 — Key Metrics Preview */}
      <div className="fold-subtle km-section">
        <div className="fold-inner">
          <p className="feyebrow" style={{ textAlign:'center', marginBottom:12 }}>What your results look like</p>
          <h2 className="fh2" style={{ textAlign:'center', marginBottom:8 }}>A score across every parameter. A finding behind every number.</h2>
          <p className="fsub" style={{ textAlign:'center', maxWidth:560, margin:'0 auto 24px' }}>Your results page breaks down performance across all three tiers. Every parameter is scored, explained, and tied to a revenue impact.</p>
          <div className="km-outer">
            <div className="km-tabbar">
              {['Key Metrics','Digital Diagnostic','Rate Parity','Action Plan'].map((label,i) => (
                <div key={i} className={`km-tab${activeKmTab===i?' km-tab-active':''}`} onClick={()=>setActiveKmTab(i)}>{label}</div>
              ))}
            </div>
            <div className="km-tabcontent">

            {/* ── KEY METRICS ── */}
            {activeKmTab === 0 && <>

              {/* Tier 1 */}
              <div className="km-tier-block">
                <div className="km-tier-header">
                  <div className="km-gauge-sm">
                    <svg viewBox="0 0 96 108" width="110" height="124">
                      <circle cx="48" cy="48" r="40" fill="none" stroke="#F5F6FA" strokeWidth="8"/>
                      <circle cx="48" cy="48" r="40" fill="none" stroke="#2D4DE0" strokeWidth="8" strokeDasharray="129.6 121.5" strokeLinecap="round" transform="rotate(-90 48 48)"/>
                      <text x="48" y="43" textAnchor="middle" fontFamily="Outfit,sans-serif" fontSize="24" fontWeight="800" fill="#0A0F2E">52</text>
                      <text x="48" y="62" textAnchor="middle" fontSize="9" fill="rgba(10,10,26,0.35)">out of 100</text>
                    </svg>
                  </div>
                  <div className="km-tier-meta">
                    <div className="km-tier-title-row">
                      <span className="km-tier-title">Market Presence &amp; Discoverability</span>
                      <span className="km-pill km-pill-weak">Weak</span>
                    </div>
                    <p className="km-tier-sub">Can guests find your hotel before OTAs capture the search?</p>
                    <div className="km-param-list">
                      {[
                        { name:'Brand Visibility & Dominance', pct:70, score:'7/10', badge:'Good', bc:'km-sb-good' },
                        { name:'Rate Parity & Channel Competitiveness', pct:20, score:'2/10', badge:'Critical', bc:'km-sb-critical' },
                        { name:'Google Business Profile Effectiveness', pct:90, score:'9/10', badge:'Strong', bc:'km-sb-strong' },
                      ].map((p,i) => (
                        <div key={i} className="km-prow">
                          <div className="km-prow-left">
                            <span className="km-pname">{p.name}</span>
                            <div className="km-pbar-wrap"><div className="km-pbar" style={{ width:`${p.pct}%` }}/></div>
                          </div>
                          <div className="km-prow-right">
                            <span className="km-pscore">{p.score}</span>
                            <span className={`km-sbadge ${p.bc}`}>{p.badge}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tier 2 */}
              <div className="km-tier-block">
                <div className="km-tier-header">
                  <div className="km-gauge-sm">
                    <svg viewBox="0 0 96 108" width="110" height="124">
                      <circle cx="48" cy="48" r="40" fill="none" stroke="#F5F6FA" strokeWidth="8"/>
                      <circle cx="48" cy="48" r="40" fill="none" stroke="#2D4DE0" strokeWidth="8" strokeDasharray="102.9 148.2" strokeLinecap="round" transform="rotate(-90 48 48)"/>
                      <text x="48" y="43" textAnchor="middle" fontFamily="Outfit,sans-serif" fontSize="24" fontWeight="800" fill="#0A0F2E">41</text>
                      <text x="48" y="62" textAnchor="middle" fontSize="9" fill="rgba(10,10,26,0.35)">out of 100</text>
                    </svg>
                  </div>
                  <div className="km-tier-meta">
                    <div className="km-tier-title-row">
                      <span className="km-tier-title">Booking Experience &amp; Friction</span>
                      <span className="km-pill km-pill-weak">Weak</span>
                    </div>
                    <p className="km-tier-sub">When guests arrive at your site, can they book without friction?</p>
                    <div className="km-param-list">
                      {[
                        { name:'Page Load Performance', pct:20, score:'2/10', badge:'Critical', bc:'km-sb-critical' },
                        { name:'Mobile Booking Experience', pct:50, score:'5/10', badge:'Weak', bc:'km-sb-weak' },
                        { name:'Booking Simplicity & Friction', pct:40, score:'4/10', badge:'Weak', bc:'km-sb-weak' },
                      ].map((p,i) => (
                        <div key={i} className="km-prow">
                          <div className="km-prow-left">
                            <span className="km-pname">{p.name}</span>
                            <div className="km-pbar-wrap"><div className="km-pbar" style={{ width:`${p.pct}%` }}/></div>
                          </div>
                          <div className="km-prow-right">
                            <span className="km-pscore">{p.score}</span>
                            <span className={`km-sbadge ${p.bc}`}>{p.badge}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tier 3 */}
              <div className="km-tier-block">
                <div className="km-tier-header">
                  <div className="km-gauge-sm">
                    <svg viewBox="0 0 96 108" width="110" height="124">
                      <circle cx="48" cy="48" r="40" fill="none" stroke="#F5F6FA" strokeWidth="8"/>
                      <circle cx="48" cy="48" r="40" fill="none" stroke="#2D4DE0" strokeWidth="8" strokeDasharray="70.4 180.7" strokeLinecap="round" transform="rotate(-90 48 48)"/>
                      <text x="48" y="43" textAnchor="middle" fontFamily="Outfit,sans-serif" fontSize="24" fontWeight="800" fill="#0A0F2E">28</text>
                      <text x="48" y="62" textAnchor="middle" fontSize="9" fill="rgba(10,10,26,0.35)">out of 100</text>
                    </svg>
                  </div>
                  <div className="km-tier-meta">
                    <div className="km-tier-title-row">
                      <span className="km-tier-title">Conversion Confidence &amp; Credibility</span>
                      <span className="km-pill km-pill-critical">Critical</span>
                    </div>
                    <p className="km-tier-sub">Are you giving guests a compelling reason to book direct?</p>
                    <div className="km-param-list">
                      {[
                        { name:'Direct Booking Incentives & Infrastructure', pct:10, score:'1/10', badge:'Critical', bc:'km-sb-critical' },
                        { name:'Content Depth & Quality', pct:60, score:'6/10', badge:'Weak', bc:'km-sb-weak' },
                        { name:'Social Proof & Review Signals', pct:50, score:'5/10', badge:'Weak', bc:'km-sb-weak' },
                        { name:'Payment Trust & Security', pct:20, score:'2/10', badge:'Critical', bc:'km-sb-critical' },
                      ].map((p,i) => (
                        <div key={i} className="km-prow">
                          <div className="km-prow-left">
                            <span className="km-pname">{p.name}</span>
                            <div className="km-pbar-wrap"><div className="km-pbar" style={{ width:`${p.pct}%` }}/></div>
                          </div>
                          <div className="km-prow-right">
                            <span className="km-pscore">{p.score}</span>
                            <span className={`km-sbadge ${p.bc}`}>{p.badge}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="km-legend">
                <span className="km-legend-item"><span className="km-sbadge km-sb-strong">Strong</span> 9-10</span>
                <span className="km-legend-item"><span className="km-sbadge km-sb-good">Good</span> 7-8</span>
                <span className="km-legend-item"><span className="km-sbadge km-sb-weak">Weak</span> 4-6</span>
                <span className="km-legend-item"><span className="km-sbadge km-sb-critical">Critical</span> 0-3</span>
              </div>
            </>}

            {/* ── DIGITAL DIAGNOSTIC ── */}
            {activeKmTab === 1 && <>
              {[
                { title:'Market Presence & Discoverability', pill:'Weak', pillCls:'km-pill-weak',
                  sub:'Can guests find your hotel before OTAs capture the search?',
                  curVal:'7,311', curLabel:'Monthly Organic Visits',
                  potVal:'14,224', potLabel:'Your Potential',
                  pct:51, available:'+6,913 visits available',
                  issues:[
                    'Your direct rate is 12.6% above the cheapest OTA — most guests verify on Google Hotels before booking, and will not come back',
                  ]},
                { title:'Booking Experience & Friction', pill:'Critical', pillCls:'km-pill-critical',
                  sub:'When guests arrive at your site, can they book without friction?',
                  curVal:'34', curLabel:'Booking Experience Score',
                  potVal:'75', potLabel:'Your Potential',
                  pct:45, available:'+41 pts available',
                  issues:[
                    'Website scores 48/100 on Google PageSpeed — guests leave before the page is usable, before seeing a reason to book direct',
                    'No single-page room comparison — a friction point OTAs eliminated years ago that consistently pushes guests to intermediaries',
                    'Tap targets too small for reliable mobile interaction — guests who misclick on a booking button rarely attempt it again',
                    'Largest element takes 14.2s to load — guests on mobile connections in your key markets will not wait',
                  ]},
                { title:'Conversion Confidence & Credibility', pill:'Critical', pillCls:'km-pill-critical',
                  sub:'Are you giving guests a compelling reason to book direct?',
                  curVal:'0.47%', curLabel:'Estimated Conversion Rate',
                  potVal:'1.50%', potLabel:'Your Potential',
                  pct:31, available:'+219% conversion available',
                  issues:[
                    'No price guarantee, no perks, no direct value proposition — your website sends guests to book elsewhere',
                    'Only 656 words indexed — your OTA listings describe your property in more detail than your own website',
                    'No payment icons detected near the booking area — first-time direct bookers need visual confirmation before entering card details',
                    'Social proof signals are not yet fully evaluated — manual review of review widget presence and recency recommended',
                  ]},
              ].map((tier,i) => (
                <div key={i} className="km-tier-block" style={{ marginBottom:10 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                    <span className="km-tier-title">{tier.title}</span>
                    <span className={`km-pill ${tier.pillCls}`}>{tier.pill}</span>
                  </div>
                  <p className="km-tier-sub" style={{ marginBottom:16 }}>{tier.sub}</p>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:2 }}>
                    <div>
                      <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:28, fontWeight:800, color:'#0A0A1A', lineHeight:1 }}>{tier.curVal}</div>
                      <div style={{ fontSize:10, color:'rgba(10,10,26,0.45)', marginTop:3 }}>{tier.curLabel}</div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:28, fontWeight:800, color:'rgba(10,10,26,0.18)', lineHeight:1 }}>{tier.potVal}</div>
                      <div style={{ fontSize:10, color:'rgba(10,10,26,0.35)', marginTop:3 }}>{tier.potLabel}</div>
                    </div>
                  </div>
                  <div style={{ height:4, background:'#E5E7EB', borderRadius:2, overflow:'hidden', margin:'10px 0 4px' }}>
                    <div style={{ height:'100%', width:`${tier.pct}%`, background:'#2D4DE0', borderRadius:2 }}/>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                    <span style={{ fontSize:11, color:'rgba(10,10,26,0.45)' }}>{tier.pct}% of potential reached</span>
                    <span style={{ fontSize:11, color:'#2D4DE0', fontWeight:600 }}>{tier.available}</span>
                  </div>
                  <span style={{ fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(10,10,26,0.45)', display:'block', marginBottom:8 }}>TOP ISSUES</span>
                  {tier.issues.map((issue,j) => (
                    <div key={j} style={{ display:'flex', gap:8, fontSize:13, color:'rgba(10,10,26,0.55)', lineHeight:1.6, marginBottom:6 }}>
                      <span style={{ width:5, height:5, borderRadius:'50%', background:'#2D4DE0', flexShrink:0, marginTop:7, display:'inline-block' }}/>
                      <span>{issue}</span>
                    </div>
                  ))}
                </div>
              ))}
            </>}

            {/* ── RATE PARITY ── */}
            {activeKmTab === 2 && <>
              {/* Header */}
              <div className="km-tier-block" style={{ display:'flex', alignItems:'center', gap:16, marginBottom:10 }}>
                <div style={{ flexShrink:0 }}>
                  <svg viewBox="0 0 96 108" width="80" height="92">
                    <circle cx="48" cy="48" r="40" fill="none" stroke="#F5F6FA" strokeWidth="8"/>
                    <circle cx="48" cy="48" r="40" fill="none" stroke="#2D4DE0" strokeWidth="8" strokeDasharray="15.1 236.2" strokeLinecap="round" transform="rotate(-90 48 48)"/>
                    <text x="48" y="43" textAnchor="middle" fontFamily="Outfit,sans-serif" fontSize="24" fontWeight="800" fill="#0A0F2E">2</text>
                    <text x="48" y="62" textAnchor="middle" fontSize="9" fill="rgba(10,10,26,0.35)">out of 100</text>
                  </svg>
                </div>
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                    <span style={{ fontFamily:"'Outfit',sans-serif", fontSize:20, fontWeight:800, color:'#0A0F2E' }}>Rate Parity Status</span>
                    <span className="km-pill km-pill-critical">Critical</span>
                  </div>
                  <p style={{ fontSize:13, color:'rgba(10,10,26,0.52)', margin:0 }}>Rate parity measures price consistency across your direct website and OTA channels.</p>
                </div>
              </div>

              {/* Rate tables */}
              {[
                { title:'Weekday Rates', checkin:'2026-05-26', rows:[
                  { channel:'Direct Website', rate:'USD 926', dev:'—', status:'Reference', sc:'rp-badge-ref', direct:true },
                  { channel:'Hotels.com',     rate:'USD 907', dev:'-2.7%',  status:'Undercut',  sc:'rp-badge-undercut' },
                  { channel:'Booking.com',    rate:'USD 1,192', dev:'+28.7%', status:'Premium', sc:'rp-badge-premium' },
                  { channel:'Expedia.co.uk',  rate:'USD 907', dev:'-2.7%',  status:'Undercut',  sc:'rp-badge-undercut' },
                  { channel:'Super.com',      rate:'USD 818', dev:'-11.7%', status:'Undercut',  sc:'rp-badge-undercut' },
                  { channel:'trivago DEALS',  rate:'USD 890', dev:'-3.9%',  status:'Undercut',  sc:'rp-badge-undercut' },
                ]},
                { title:'Weekend Rates', checkin:'2026-05-29', rows:[
                  { channel:'Direct Website', rate:'USD 929', dev:'—', status:'Reference', sc:'rp-badge-ref', direct:true },
                  { channel:'Expedia.co.uk',  rate:'USD 910', dev:'-2.0%',  status:'Undercut',  sc:'rp-badge-undercut' },
                  { channel:'Hotels.com',     rate:'USD 910', dev:'-2.0%',  status:'Undercut',  sc:'rp-badge-undercut' },
                  { channel:'Booking.com',    rate:'USD 936', dev:'+0.8%',  status:'Parity',    sc:'rp-badge-parity' },
                  { channel:'Super.com',      rate:'USD 825', dev:'-11.2%', status:'Undercut',  sc:'rp-badge-undercut' },
                  { channel:'trivago DEALS',  rate:'USD 891', dev:'-4.1%',  status:'Undercut',  sc:'rp-badge-undercut' },
                ]},
              ].map((table,ti) => (
                <div key={ti} className="km-tier-block" style={{ padding:0, overflow:'hidden', marginBottom:10 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px 10px' }}>
                    <span style={{ fontFamily:"'Outfit',sans-serif", fontSize:15, fontWeight:700, color:'#0A0F2E' }}>{table.title}</span>
                    <span style={{ fontSize:11, color:'rgba(10,10,26,0.35)' }}>Check-in: {table.checkin}</span>
                  </div>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr>
                        {['Channel','Rate','Deviation','Status'].map((h,hi) => (
                          <th key={hi} style={{ fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(10,10,26,0.35)', padding:'6px 16px', textAlign: hi===3?'right':'left', borderBottom:'1px solid rgba(10,10,26,0.06)', borderTop:'1px solid rgba(10,10,26,0.06)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {table.rows.map((row,ri) => (
                        <tr key={ri} style={{ borderBottom: ri<table.rows.length-1 ? '1px solid rgba(10,10,26,0.05)' : 'none' }}>
                          <td style={{ padding:'10px 16px', fontSize:13, fontWeight: row.direct?700:500, color: row.direct?'#0A0F2E':'#2D4DE0' }}>{row.channel}</td>
                          <td style={{ padding:'10px 16px', fontFamily:"'Outfit',sans-serif", fontSize:13, fontWeight:700, color:'#0A0F2E' }}>{row.rate}</td>
                          <td style={{ padding:'10px 16px', fontSize:12, fontWeight:600, color: row.dev==='—'?'rgba(10,10,26,0.35)': row.dev.startsWith('-')?'#EF4444':'#F59E0B' }}>{row.dev}</td>
                          <td style={{ padding:'10px 16px', textAlign:'right' }}>
                            <span className={`rp-badge ${row.sc}`}>{row.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </>}

            {/* ── ACTION PLAN ── */}
            {activeKmTab === 3 && <>
              <div style={{ marginBottom:14 }}>
                <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:18, fontWeight:800, color:'#0A0F2E', marginBottom:4 }}>Your Prioritised Action Plan</div>
                <p style={{ fontSize:13, color:'rgba(10,10,26,0.52)', lineHeight:1.6, margin:0 }}>8 actions ranked by revenue recovery potential — fixing the top 2 alone addresses over 30% of your total weighted score.</p>
              </div>

              {/* Priority counters */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:14 }}>
                {[
                  { num:4, label:'High Priority',   bg:'#F0F1F7',               numColor:'#0A0F2E' },
                  { num:3, label:'Medium Priority',  bg:'#FFF7ED',               numColor:'#EA580C' },
                  { num:1, label:'Low Priority',     bg:'rgba(10,10,26,0.04)',    numColor:'rgba(10,10,26,0.40)' },
                ].map((c,i) => (
                  <div key={i} style={{ background:c.bg, borderRadius:12, padding:'14px 16px' }}>
                    <span style={{ fontFamily:"'Outfit',sans-serif", fontSize:28, fontWeight:800, color:c.numColor, display:'block', lineHeight:1 }}>{c.num}</span>
                    <span style={{ fontSize:12, color:'rgba(10,10,26,0.52)', marginTop:4, display:'block' }}>{c.label}</span>
                  </div>
                ))}
              </div>

              {/* Action item 1 */}
              <div className="km-tier-block" style={{ marginBottom:10 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                  <span style={{ width:24, height:24, borderRadius:'50%', background:'#2D4DE0', color:'#fff', fontSize:12, fontWeight:700, display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>1</span>
                  <span style={{ fontFamily:"'Outfit',sans-serif", fontSize:16, fontWeight:700, color:'#0A0F2E' }}>Simplify the Booking Process</span>
                </div>
                <div style={{ display:'flex', gap:6, marginBottom:12 }}>
                  <span style={{ fontSize:'9.5px', fontWeight:600, borderRadius:4, padding:'2px 8px', background:'rgba(45,77,224,0.08)', color:'#2D4DE0' }}>Booking Experience &amp; Friction</span>
                  <span style={{ fontSize:'9.5px', fontWeight:600, borderRadius:4, padding:'2px 8px', background:'#F0F1F7', color:'#0A0A1A' }}>High Impact</span>
                </div>
                <p style={{ fontSize:13, color:'rgba(10,10,26,0.55)', lineHeight:1.7, marginBottom:14 }}>The direct booking flow imposes more friction than any major OTA. Guests who compare the experience will not return to book direct — regardless of price or incentive.</p>
                <div style={{ padding:'13px 16px', background:'rgba(15,42,138,0.04)', border:'1px solid rgba(15,42,138,0.08)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, marginBottom:14 }}>
                  <div>
                    <span style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', color:'rgba(10,10,26,0.30)', display:'block', marginBottom:4 }}>REVENUE RECOVERY POTENTIAL</span>
                    <span style={{ fontSize:11, color:'rgba(10,10,26,0.40)' }}>Estimated annual direct revenue recoverable by closing this gap.</span>
                  </div>
                  <span style={{ fontFamily:"'Outfit',sans-serif", fontSize:26, fontWeight:800, color:'#0F2A8A', letterSpacing:'-1px', flexShrink:0 }}>$97K</span>
                </div>
                <span style={{ fontSize:9, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(10,10,26,0.45)', display:'block', marginBottom:8 }}>CRITICAL FINDINGS</span>
                {[
                  'No single-page room comparison — a friction point OTAs eliminated years ago that consistently pushes guests to intermediaries',
                  'Booking CTA not visible above the fold — the most important conversion action on the website is invisible to guests on arrival',
                ].map((f,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, padding:'8px 0', borderBottom: i===0?'1px solid rgba(10,10,26,0.05)':'none' }}>
                    <span style={{ fontSize:13, color:'rgba(10,10,26,0.55)', lineHeight:1.5, flex:1 }}>{f}</span>
                    <span style={{ fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:4, background:'rgba(220,38,38,0.08)', color:'#DC2626', flexShrink:0 }}>Critical</span>
                  </div>
                ))}
              </div>

              {/* Locked items */}
              <div style={{ position:'relative' }}>
                {['Rate Parity & Channel Competitiveness','Page Load Performance','Direct Booking Incentive','Content Depth & Quality','Mobile Booking Experience','Social Proof & Review Signals','Payment Trust & Security'].map((name,i) => (
                  <div key={i} style={{ padding:'12px 16px', background:'#fff', border:'1px solid rgba(10,10,26,0.08)', borderLeft:'3px solid rgba(10,10,26,0.10)', borderRadius:12, marginBottom:6, opacity:0.45, fontSize:14, fontWeight:600, color:'rgba(10,10,26,0.45)', display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ width:22, height:22, borderRadius:'50%', background:'rgba(10,10,26,0.10)', fontSize:11, fontWeight:700, color:'rgba(10,10,26,0.35)', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{i+2}</span>
                    {name}
                  </div>
                ))}
                <div style={{ background:'#fff', border:'1px solid rgba(10,10,26,0.08)', borderRadius:14, padding:'24px', textAlign:'center', marginTop:-50, position:'relative', zIndex:2, boxShadow:'0 -24px 40px rgba(255,255,255,0.98), 0 4px 16px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:16, fontWeight:800, color:'#0A0F2E', marginBottom:6 }}>Unlock Your Full Action Plan</div>
                  <p style={{ fontSize:13, color:'rgba(10,10,26,0.52)', marginBottom:16, lineHeight:1.5 }}>Book a free 30-min call with a dhi Hospitality expert to get your complete roadmap.</p>
                  <button style={{ background:'#2D4DE0', color:'#fff', border:'none', borderRadius:10, padding:'12px 28px', fontFamily:"'Outfit',sans-serif", fontSize:14, fontWeight:700, cursor:'pointer' }}>Book a Free Strategy Call</button>
                </div>
              </div>
            </>}

            </div>
          </div>
        </div>
      </div>

      {/* CTA STRIP 3 — after km-section */}
      <div className="cta-strip">
        <div>
          <p className="cta-strip-text">Built exclusively for hotels. By hoteliers.</p>
          <p className="cta-strip-sub">The same rigour your commercial team applies to revenue strategy, applied to your digital infrastructure.</p>
        </div>
        <button className="cta-btn" onClick={() => scrollToForm()}>Start Your Audit →</button>
      </div>

      {/* FOLD 5 — FAQ */}
      <div className="fold-subtle" style={{ paddingTop:80 }} ref={faqSectionRef}>
        <div className="fold-inner">
          <div className="faq-grid">
            <div className="faq-header" ref={faqHeaderRef} style={{ willChange:'transform' }}>
              <p className="feyebrow">Frequently Asked</p>
              <h2 className="fh2" style={{ marginBottom:20 }}>Answers, before you ask.</h2>
              <p className="fsub" style={{ marginBottom:0 }}>The audit, the methodology, the numbers — covered.</p>
            </div>
            <div className="faq-list" ref={faqListRef}>
              {[
                { q:"How does this audit help increase my hotel's direct bookings?", a:'It identifies precisely where your hotel\'s digital infrastructure is losing guests to OTAs and puts a revenue figure on each gap. You leave with a ranked action plan so your team knows exactly what to fix and in what order.' },
                { q:"How is my hotel's digital performance benchmarked?", a:'Against over 1,000 hotel audits across independent properties and regional groups worldwide, calibrated separately for city and leisure assets. Your score reflects what is genuinely achievable for a property of your type and market.' },
                { q:'How accurate is the estimated traffic and revenue opportunity?', a:'All data is pulled live at the time of your audit from authoritative industry sources. Revenue projections are modelled against your property inputs and industry conversion benchmarks. They are directional estimates intended as a strategic planning guide, clearly presented as such in your report.' },
                { q:'What are the 10 parameters used to audit my hotel?', a:'Your hotel is assessed across three tiers: Market Presence and Discoverability, Booking Experience and Friction, and Conversion Confidence and Credibility. Each tier contains parameters weighted by their direct impact on revenue. Run the audit to see your full breakdown.' },
                { q:'Do I have to pay for the audit or the action plan?', a:'The audit and your scored report are yours at no cost. Your prioritised action plan is walked through in a focused 30-minute session with a dhi specialist — so you leave knowing exactly what to do, in what order, and why it matters for your property specifically.' },
                { q:'I use a leading booking engine. Why is my conversion score still low?', a:"A recognised booking engine does not guarantee performance. What matters is how it is configured and where it sits in your guest's journey. External redirects, too many steps, and weak trust signals at checkout are common causes of drop-off regardless of the engine brand. The audit measures the actual guest experience." },
                { q:'How long does it take to see results after acting on the recommendations?', a:'Rate corrections, Google Business Profile updates, and CTA improvements can reflect within weeks. Structural changes typically show measurable impact within 60 to 90 days. The action plan is ranked by priority — High, Medium, and Low — so you can sequence accordingly.' },
                { q:'Can independent hotels truly compete with OTAs for direct bookings?', a:'Yes. The hotels that do are not outspending OTAs. They have fixed the fundamentals — competitive direct rates, strong brand search presence, a frictionless booking experience, and a clear reason to book direct. None of these require significant investment. They require focus, and the audit tells you exactly where yours needs to go.' },
              ].map((item, i) => (
                <details key={i} className="faq-item">
                  <summary>
                    <span className="fq-num">{String(i+1).padStart(2,'0')}</span>
                    <span className="fqtext">{item.q}</span>
                    <span className="fqchev" aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </span>
                  </summary>
                  <div className="fqa-wrap">
                    <p className="fqa">{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA STRIP — Final conversion point before footer */}
      <div className="cta-strip">
        <div>
          <p className="cta-strip-text">Ready to see where your direct revenue is going?</p>
          <p className="cta-strip-sub">Free audit. 45 seconds. No login required.</p>
        </div>
        <button className="cta-btn" onClick={() => scrollToForm()}>Run Your Audit →</button>
      </div>

      {/* Inline keyframes for loading spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Sections */
        .fold-dark { background: #0A0A1A !important; padding: 80px 48px; position: relative; overflow: hidden; }
        .fold-light { background: #fff !important; padding: 80px 48px; position: relative; overflow: hidden; }
        .fold-subtle { background: #F8F9FF !important; padding: 80px 48px; position: relative; overflow: hidden; }
        .fold-receive { background: #F5F6FA !important; padding: 80px 48px; position: relative; overflow: hidden; }
        .fold-inner { max-width: 1100px; margin: 0 auto; position: relative; z-index: 1; }

        .feyebrow { font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--blue) !important; margin-bottom: 16px; }
        .fh2 { font-family: 'Outfit', sans-serif; font-size: 34px; font-weight: 800; color: var(--ink) !important; margin-bottom: 24px; line-height: 1.1; }
        .fsub { font-size: 15px; color: var(--ink-50) !important; margin-bottom: 48px; line-height: 1.7; max-width: 700px; }

        /* Dark fold text overrides */
        .fold-dark .feyebrow { color: rgba(255,255,255,0.4) !important; margin-bottom: 10px !important; }
        .fold-dark .fh2 { color: #fff !important; font-size: 34px; margin-bottom: 10px; }
        .fold-dark .fsub { color: rgba(255,255,255,0.5) !important; font-size: 15px; max-width: 600px; margin-bottom: 44px; }

        /* CTA Strip */
        .cta-strip { background: #0D1640 !important; padding: 28px 48px; display: flex; align-items: center; justify-content: space-between; gap: 24px; }
        .cta-strip-text { font-family: 'Outfit', sans-serif; font-size: 19px; font-weight: 800; color: #fff !important; margin-bottom: 4px; }
        .cta-strip-sub { font-size: 13px; color: rgba(255,255,255,0.5) !important; }
        .cta-btn { background: #fff !important; border: none; border-radius: 10px; padding: 13px 26px; font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 700; color: #0A0A1A !important; cursor: pointer; white-space: nowrap; flex-shrink: 0; }

        /* Steps */
        .steps-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 32px; margin-bottom: 52px; }
        .step { text-align: center; }
        .snum { width: 56px; height: 56px; border-radius: 16px; background: var(--blue-tint) !important; border: 1px solid var(--blue-border) !important; font-family: 'Outfit', sans-serif; font-size: 20px; font-weight: 800; color: var(--blue) !important; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; }
        .stitle { font-family: 'Outfit', sans-serif; font-size: 18px; font-weight: 800; color: var(--ink) !important; margin-bottom: 12px; line-height: 1.3; }
        .sdesc { font-size: 14px; color: var(--ink-50) !important; line-height: 1.6; }

        /* Parameters grid */
        .params-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 16px; }
        .pi { background: var(--white) !important; border: 1px solid var(--ink-08) !important; border-radius: 16px; padding: 20px; display: flex; align-items: flex-start; gap: 16px; transition: all 0.3s ease; }
        .pi:hover { background: var(--off-white) !important; border-color: var(--blue-border) !important; }
        .pinum { width: 30px; height: 30px; border-radius: 8px; background: var(--blue-tint) !important; border: 1px solid var(--blue-border) !important; font-size: 12px; font-weight: 800; color: var(--blue) !important; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .piname { font-size: 15px; font-weight: 700; color: var(--ink) !important; margin-bottom: 4px; }
        .pidesc { font-size: 13px; color: var(--ink-50) !important; line-height: 1.4; }

        /* Dashboard preview grid layout */
        .dash-row1 { display: grid; grid-template-columns: 280px 1fr; gap: 16px; margin-bottom: 16px; }
        .dash-row2 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }

        .km-section { background: transparent !important; padding-top: 80px; padding-bottom: 80px; }
        .km-section .fh2 { font-family: 'Outfit', sans-serif; font-size: 34px; }
        .km-section .fsub { font-size: 15px; }

        /* Key Metrics section — results page theme */
        .km-outer { background: #fff !important; border-radius: 14px; border: 1px solid rgba(10,10,26,0.08) !important; overflow: hidden; max-width: 980px; margin: 24px auto 60px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
        .km-tabbar { display: flex; gap: 3px; padding: 4px; background: #ffffff !important; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; border-bottom: 1px solid rgba(10,10,26,0.08); }
        .km-tab { flex: 1; min-width: 90px; padding: 8px 4px; font-family: 'DM Sans', sans-serif; font-size: 11px; font-weight: 600; color: rgba(10,10,26,0.40) !important; text-align: center; white-space: nowrap; flex-shrink: 0; border-radius: 7px; cursor: pointer; transition: background 0.15s, color 0.15s; }
        .km-tab-active { background: #0A0A1A !important; color: #ffffff !important; border-bottom: none; }
        .km-tabcontent { padding: 16px; background: #F5F6FA !important; }
        .km-tier-block { background: #fff !important; border-radius: 14px; border: 1px solid rgba(10,10,26,0.08) !important; border-left: 3px solid #2D4DE0 !important; padding: 18px; margin-bottom: 10px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
        .km-tier-header { display: flex; align-items: flex-start; gap: 20px; }
        .km-gauge-sm { flex-shrink: 0; width: 110px; display: flex; align-items: center; justify-content: center; padding-top: 4px; }
        .km-tier-meta { flex: 1; min-width: 0; padding-top: 4px; }
        .km-tier-title-row { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 4px; }
        .km-tier-title { font-family: 'Outfit', sans-serif; font-size: 18px; font-weight: 800; color: #0A0F2E !important; }
        .km-tier-sub { font-size: 13px; color: rgba(10,10,26,0.52) !important; line-height: 1.5; margin-top: 2px; margin-bottom: 0; }
        .km-pill { font-size: 10px; font-weight: 700; padding: 4px 11px; border-radius: 20px; flex-shrink: 0; display: inline-block; background: #F0F1F7; color: #0A0F2E; }
        .km-pill-weak { background: #F0F1F7; color: #0A0F2E; }
        .km-pill-critical { background: #F0F1F7; color: #0A0F2E; }
        .km-param-list { display: flex; flex-direction: column; margin-top: 14px; }
        .km-prow { display: flex; align-items: center; gap: 16px; padding: 10px 0; }
        .km-prow:last-child { padding-bottom: 0; }
        .km-prow-left { flex: 1; min-width: 0; }
        .km-prow-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; width: 120px; justify-content: flex-end; }
        .km-pname { font-size: 13px; font-weight: 500; color: rgba(10,10,26,0.55) !important; line-height: 1.3; display: block; margin-bottom: 6px; }
        .km-pbar-wrap { width: 75%; height: 3px; background: #E5E7EB !important; border-radius: 2px; overflow: hidden; }
        .km-pbar { height: 3px; border-radius: 2px; background: #2D4DE0 !important; }
        .km-pscore { font-weight: 700; flex-shrink: 0; font-size: 13px; color: rgba(10,10,26,0.45) !important; white-space: nowrap; width: 36px; text-align: right; }
        .km-sbadge { font-size: 9px; font-weight: 700; padding: 2px 0; border-radius: 20px; width: 56px; text-align: center; flex-shrink: 0; display: inline-block; }
        .km-sb-strong { background: #EBF0FF; color: #2D4DE0; }
        .km-sb-good   { background: #EBF0FF; color: #2D4DE0; }
        .km-sb-weak   { background: #F0F1F7; color: #0A0F2E; }
        .km-sb-critical { background: #F0F1F7; color: #0A0F2E; }
        .km-legend { display: flex; justify-content: center; align-items: center; gap: 16px; padding: 12px 0 4px; flex-wrap: wrap; }
        .km-legend-item { display: flex; align-items: center; gap: 6px; font-size: 12px; color: rgba(10,10,26,0.55) !important; }

        /* Rate Parity badges */
        .rp-badge { font-size: 10px; font-weight: 600; padding: 2px 10px; border-radius: 20px; display: inline-block; }
        .rp-badge-ref     { background: #EBF0FF; color: #2D4DE0; border: 1px solid rgba(45,77,224,0.2); }
        .rp-badge-undercut{ background: rgba(239,68,68,0.08); color: #EF4444; border: 1px solid rgba(239,68,68,0.2); }
        .rp-badge-premium { background: rgba(245,158,11,0.08); color: #F59E0B; border: 1px solid rgba(245,158,11,0.2); }
        .rp-badge-parity  { background: rgba(34,197,94,0.08);  color: #16a34a; border: 1px solid rgba(34,197,94,0.2); }

        /* FAQ — Editorial two-column layout */
        .faq-grid { display: grid; grid-template-columns: 1fr 1.35fr; gap: 80px; max-width: 1100px; margin: 0 auto; align-items: start; }
        .faq-header { position: relative; top: 0; }
        .faq-header .fh2 { font-size: 38px; line-height: 1.05; }

        .faq-list { display: flex; flex-direction: column; gap: 12px; }
        .faq-item { background: #fff; border: 1px solid rgba(26,47,168,0.3); border-radius: 14px; overflow: hidden; position: relative; transition: border-color 0.25s, box-shadow 0.25s; box-shadow: 0 2px 6px rgba(0,0,0,0.08), 0 6px 20px rgba(0,0,0,0.11); }
        .faq-item::before { content: ""; position: absolute; top: 0; left: 20px; right: 20px; height: 2px; background: linear-gradient(90deg, #1A2FA8, #2D4DE0, rgba(26,47,168,0)); border-radius: 0 0 2px 2px; z-index: 1; }
        .faq-item:hover { border-color: rgba(26,47,168,0.55); box-shadow: 0 4px 10px rgba(0,0,0,0.11), 0 10px 28px rgba(0,0,0,0.14); }
        .faq-item[open] { border-color: rgba(26,47,168,0.6); }
        .faq-item summary { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 20px; padding: 22px 26px; cursor: pointer; list-style: none; }
        .faq-item summary::-webkit-details-marker { display: none; }
        .fq-num { font-size: 11px; font-weight: 800; letter-spacing: 0.15em; color: #1A2FA8; opacity: 0.9; min-width: 22px; }
        .fqtext { font-family: 'Outfit', sans-serif; font-size: 16px; font-weight: 700; color: #020412; margin: 0; line-height: 1.4; }
        .fqchev { width: 34px; height: 34px; border-radius: 50%; background: rgba(26,47,168,0.1); border: 1px solid rgba(26,47,168,0.3); color: #1A2FA8; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: transform 0.3s, background 0.25s, border-color 0.25s; }
        .faq-item:hover .fqchev { background: rgba(26,47,168,0.15); border-color: rgba(26,47,168,0.5); }
        .faq-item[open] .fqchev { transform: rotate(45deg); background: #1A2FA8; color: #fff; border-color: #1A2FA8; }
        .fqa-wrap { padding: 0 26px 24px 68px; }
        .fqa { font-size: 14.5px; color: rgba(2,4,18,0.55); line-height: 1.75; margin: 0; }

        @media(max-width: 900px) {
          .faq-grid { grid-template-columns: 1fr; gap: 40px; }
          .faq-header { position: static; }
          .faq-header .fh2 { font-size: 30px; }
          .fqa-wrap { padding-left: 26px; }
        }

        /* Mobile */
        @media(max-width:768px){
          .fold-dark,.fold-light,.fold-subtle,.fold-receive { padding: 40px 20px; }
          .cta-strip { padding: 24px 20px; flex-direction: column; align-items: flex-start; gap: 14px; }
          .cta-btn { width: 100%; text-align: center; }
          .cta-strip-text { font-size: 17px; }
          .fh2 { font-size: 22px; }
          .fsub { font-size: 14px; }
          .steps-grid { grid-template-columns: 1fr; }
          .params-grid { grid-template-columns: 1fr; }
          .dash-row1 { grid-template-columns: 1fr; }
          .dash-row2 { grid-template-columns: 1fr; }
          .km-section { padding-top: 40px; }
          .km-tab { font-size: 11px; padding: 11px 6px; min-width: 80px; }
          .km-tier-header { gap: 12px; }
          .km-gauge-sm { width: 80px; }
          .km-gauge-sm svg { width: 80px; height: 92px; }
          .km-tier-title { font-size: 15px; }
          .km-pname { font-size: 12px; }
          .km-pbar-wrap { width: 50%; }
          .km-sbadge { font-size: 9px; width: 50px; }
          .fqtext { font-size: 14px; }
          .fqa { font-size: 13px; }
        }

        @media(max-width:480px){
          .fold-dark,.fold-light,.fold-subtle,.fold-receive { padding: 32px 16px; }
          .km-tier-header { flex-direction: column; }
        }
      `}</style>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;
